import React from 'react';
import ReactTooltip from 'react-tooltip';
import { oneDecimal } from '../../helpers/formatters';
import styles from './region-entity-table.module.scss';
import { connect } from 'react-redux';
import * as selectors from '../../reducers';
import PropTypes from 'prop-types';
import {
  setActiveEntity,
  setComparedEntity,
  setActivePage,
} from '../../actions/ui';
import { RegionTableHeader } from './region-table-header';
import { ArrowRight } from '../../components/icons';

const SCHOOL_TYPES = [
  'Primary',
  'Secondary',
  'Independent',
  'Combined',
  'Special',
];

const TOOLTIPS = {
  Primary:
    'Primary schools provide students with compulsory learning from Prep to Year 6.',
  Secondary:
    'Secondary schools, or high schools, provide education programs for students from Year 7 to Year 12.',
  Independent:
    'Independent Public Schools can include schools from any other category. They are afforded greater autonomy in decision-making and increased capacity to work in new ways to maximise learning outcomes.',
  Combined:
    "Combined schools generally provide education to students from Prep to Year 10 or Year 12, depending on the school's location.",
  Special:
    'Special schools provide highly specialised and individual programs for students with significant support needs.',
};

const RegionTableComponent = ({
  region,
  setActiveEntity,
  setComparedEntity,
  setActivePage,
  isMobile,
}) => {
  return (
    <div style={{ width: '100%' }}>
      <div style={{ width: '100%', overflow: 'auto' }}>
        <table className={styles.entityRegionTable}>
          <tbody>
            <RegionTableHeader
              region={region}
              comparing={false}
              isMobile={isMobile}
            />
            {region.byRegionByEntity.map((e, i) => {
              const hasCampuses =
                e.entityCategory === 'Universities' ||
                e.entity
                  .toLocaleLowerCase()
                  .includes('department of employment') ||
                e.entity.includes('TAFE');
              return (
                <tr className={styles.regularTableRow} key={e + i}>
                  {!isMobile && (
                    <td className={styles.institutionType}>
                      {/* Only show the category if it's the first instance in the table */}
                      {region.byRegionByEntity.indexOf(
                        region.byRegionByEntity.find(
                          ({ orderingCategory }) =>
                            orderingCategory === e.orderingCategory
                        )
                      ) === i
                        ? e.orderingCategory
                        : ''}
                    </td>
                  )}

                  <td
                    style={{ minWidth: 200 }}
                    onClick={() => {
                      setActiveEntity(e.entity);
                      setComparedEntity(null);
                      setActivePage('entity');
                    }}
                    data-clickable={true}
                  >
                    {e.entity}
                  </td>
                  <td className={styles.numberCell}>
                    {hasCampuses ? e.numberOfCampuses.toLocaleString() : ''}
                  </td>
                  <td className={styles.numberCell}>
                    {!hasCampuses
                      ? `${e.numberOfCampuses.toLocaleString()}`
                      : ''}
                  </td>
                  <td className={styles.numberCell}>
                    {oneDecimal(e.numberOfStudents).toLocaleString()}
                  </td>
                  <td className={styles.numberCell}>
                    {oneDecimal(e.numberOfEmployees).toLocaleString()}
                  </td>
                </tr>
              );
            })}

            {/* Department of Education */}
            <tr
              className={styles.regularTableRow}
              key={'department-of-education-total'}
            >
              {!isMobile && (
                <td className={styles.institutionType}>
                  Department of Education
                </td>
              )}

              <td
                style={{ minWidth: 200 }}
                data-clickable={false}
                className={styles.boldCell}
              >
                Total
              </td>
              <td className={styles.numberCell + ' ' + styles.boldCell}></td>
              <td className={styles.numberCell + ' ' + styles.boldCell}>
                {oneDecimal(region.schoolsByRegion.length).toLocaleString()}
              </td>
              <td className={styles.numberCell + ' ' + styles.boldCell}>
                {oneDecimal(
                  region.schoolsByRegion
                    .filter((d) => d.eftsl !== '')
                    .reduce((a, b) => a + b.eftsl, 0)
                ).toLocaleString()}
              </td>
              <td className={styles.numberCell + ' ' + styles.boldCell}>
                {oneDecimal(
                  region.schoolsByRegion
                    .filter((d) => d.ftEs !== '')
                    .reduce((a, b) => a + b.ftEs, 0)
                ).toLocaleString()}
              </td>
            </tr>

            {SCHOOL_TYPES.map((schoolType, i) => {
              const schoolData = region.schoolsByRegion.filter(
                (d) => d.schoolType === schoolType
              );
              return (
                <tr className={styles.regularTableRow} key={schoolType + i}>
                  {!isMobile && <td className={styles.institutionType}></td>}

                  <td style={{ minWidth: 200 }} data-clickable={false}>
                    <span
                      data-tip={schoolType}
                      data-for={schoolType}
                      className={styles.popupLabel}
                    >
                      {schoolType}
                    </span>
                    <ReactTooltip
                      aria-haspopup="true"
                      type="light"
                      id={schoolType}
                      className={styles.popup}
                      key={schoolType}
                      effect="solid"
                      arrowColor="#FFFFFF00"
                    >
                      {TOOLTIPS[schoolType]}
                    </ReactTooltip>
                  </td>
                  <td className={styles.numberCell}></td>
                  <td className={styles.numberCell}>
                    {oneDecimal(schoolData.length).toLocaleString()}
                    <sup className={styles.schoolTypeNote}>
                      {['a', 'b', 'c', 'd', 'e'][i]}
                    </sup>
                  </td>
                  <td className={styles.numberCell}>
                    {oneDecimal(
                      schoolData
                        .filter((d) => d.eftsl !== '')
                        .reduce((a, b) => a + b.eftsl, 0)
                    ).toLocaleString()}
                  </td>
                  <td className={styles.numberCell}>
                    {oneDecimal(
                      schoolData
                        .filter((d) => d.ftEs !== '')
                        .reduce((a, b) => a + b.ftEs, 0)
                    ).toLocaleString()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {isMobile && (
        <div style={{ textAlign: 'left', marginTop: 20 }}>
          <span className={styles.cellInfoText}>
            Scroll the table to see more
          </span>
          <ArrowRight
            fill="blue"
            width="10px"
            height="10px"
            style={{ marginLeft: 10 }}
          />
        </div>
      )}
    </div>
  );
};

const mapDispatchToProps = {
  setActiveEntity,
  setComparedEntity,
  setActivePage,
};

const mapStateToProps = (state, props) => ({
  isMobile: selectors.getIsMobile(state),
});

RegionTableComponent.propTypes = {
  region: PropTypes.object,
  setActiveEntity: PropTypes.func,
  setActivePage: PropTypes.func,
  setComparedEntity: PropTypes.func,
};

const RegionTable = connect(
  mapStateToProps,
  mapDispatchToProps
)(RegionTableComponent);
export { RegionTable };



// WEBPACK FOOTER //
// ./src/components/region-entity-table/region-table.js